import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { Refresh, Add, WorkOutline, Groups, EmojiEvents, PersonSearch } from '@mui/icons-material';
import {
  PageHeader,
  StatCard,
  FilterBar,
  FilterSpacer,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  StatusDot,
  StatusTone,
  TableEmptyState,
} from '../../components/common/pageKit';
import {
  hrService,
  Department,
  HrSummary,
  JobApplication,
  JobApplicationStage,
  JobOpening,
  JobOpeningStatus,
} from '../../services/hrService';

const openingStatusTone: Record<JobOpeningStatus, StatusTone> = {
  OPEN: 'success',
  ON_HOLD: 'warning',
  CLOSED: 'neutral',
  FILLED: 'info',
};

const openingStatusLabel: Record<JobOpeningStatus, string> = {
  OPEN: 'Open',
  ON_HOLD: 'On Hold',
  CLOSED: 'Closed',
  FILLED: 'Filled',
};

const stageTone: Record<JobApplicationStage, StatusTone> = {
  APPLIED: 'neutral',
  SCREENING: 'info',
  INTERVIEW: 'info',
  OFFER: 'warning',
  HIRED: 'success',
  REJECTED: 'error',
};

const stageLabel: Record<JobApplicationStage, string> = {
  APPLIED: 'Applied',
  SCREENING: 'Screening',
  INTERVIEW: 'Interview',
  OFFER: 'Offer',
  HIRED: 'Hired',
  REJECTED: 'Rejected',
};

const stages: JobApplicationStage[] = ['APPLIED', 'SCREENING', 'INTERVIEW', 'OFFER', 'HIRED', 'REJECTED'];

const RecruitmentPage: React.FC = () => {
  const [summary, setSummary] = useState<HrSummary | null>(null);
  const [departments, setDepartments] = useState<Department[]>([]);

  const [openings, setOpenings] = useState<JobOpening[]>([]);
  const [openingsLoading, setOpeningsLoading] = useState(true);
  const [openingsError, setOpeningsError] = useState<string | null>(null);
  const [openingStatusFilter, setOpeningStatusFilter] = useState<JobOpeningStatus | 'ALL'>('ALL');

  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [applicationsLoading, setApplicationsLoading] = useState(true);
  const [applicationsError, setApplicationsError] = useState<string | null>(null);
  const [stageTab, setStageTab] = useState<JobApplicationStage | 'ALL'>('ALL');
  const [openingFilter, setOpeningFilter] = useState<number | ''>('');
  const [stageUpdating, setStageUpdating] = useState<number | null>(null);

  const [openingDialogOpen, setOpeningDialogOpen] = useState(false);
  const [openingTitle, setOpeningTitle] = useState('');
  const [openingDept, setOpeningDept] = useState<number | ''>('');
  const [openingCount, setOpeningCount] = useState(1);
  const [openingTargetDate, setOpeningTargetDate] = useState('');
  const [openingBusy, setOpeningBusy] = useState(false);
  const [openingFormError, setOpeningFormError] = useState<string | null>(null);

  const [applicationDialogOpen, setApplicationDialogOpen] = useState(false);
  const [applicantOpeningId, setApplicantOpeningId] = useState<number | ''>('');
  const [applicantName, setApplicantName] = useState('');
  const [applicantEmail, setApplicantEmail] = useState('');
  const [applicantPhone, setApplicantPhone] = useState('');
  const [applicationBusy, setApplicationBusy] = useState(false);
  const [applicationFormError, setApplicationFormError] = useState<string | null>(null);

  const loadOpenings = () => {
    setOpeningsLoading(true);
    setOpeningsError(null);
    hrService
      .getJobOpenings()
      .then((res) => setOpenings(res.jobOpenings))
      .catch((err: any) => setOpeningsError(err.message || 'Failed to load job openings'))
      .finally(() => setOpeningsLoading(false));
  };

  const loadApplications = () => {
    setApplicationsLoading(true);
    setApplicationsError(null);
    hrService
      .getJobApplications()
      .then((res) => setApplications(res.jobApplications))
      .catch((err: any) => setApplicationsError(err.message || 'Failed to load applications'))
      .finally(() => setApplicationsLoading(false));
  };

  useEffect(() => {
    loadOpenings();
    loadApplications();
    hrService.getSummary().then(setSummary).catch(() => undefined);
    hrService.getDepartments().then((res) => setDepartments(res.departments)).catch(() => undefined);
  }, []);

  const stageCounts = useMemo(() => {
    const c: Record<JobApplicationStage, number> = { APPLIED: 0, SCREENING: 0, INTERVIEW: 0, OFFER: 0, HIRED: 0, REJECTED: 0 };
    applications.forEach((a) => {
      c[a.stage] += 1;
    });
    return c;
  }, [applications]);

  const filteredOpenings = openingStatusFilter === 'ALL' ? openings : openings.filter((o) => o.status === openingStatusFilter);

  const filteredApplications = applications.filter((a) => {
    if (stageTab !== 'ALL' && a.stage !== stageTab) return false;
    if (openingFilter !== '' && a.job_opening_id !== openingFilter) return false;
    return true;
  });

  const openOpeningDialog = () => {
    setOpeningTitle('');
    setOpeningDept('');
    setOpeningCount(1);
    setOpeningTargetDate('');
    setOpeningFormError(null);
    setOpeningDialogOpen(true);
  };

  const handleCreateOpening = async () => {
    if (!openingTitle.trim()) {
      setOpeningFormError('Title is required');
      return;
    }
    setOpeningBusy(true);
    setOpeningFormError(null);
    try {
      await hrService.createJobOpening({
        title: openingTitle,
        departmentId: openingDept === '' ? undefined : openingDept,
        openings: openingCount,
        targetDate: openingTargetDate || undefined,
      });
      setOpeningDialogOpen(false);
      loadOpenings();
      hrService.getSummary().then(setSummary).catch(() => undefined);
    } catch (err: any) {
      setOpeningFormError(err.message || 'Failed to create job opening');
    } finally {
      setOpeningBusy(false);
    }
  };

  const openApplicationDialog = () => {
    setApplicantOpeningId('');
    setApplicantName('');
    setApplicantEmail('');
    setApplicantPhone('');
    setApplicationFormError(null);
    setApplicationDialogOpen(true);
  };

  const handleCreateApplication = async () => {
    if (!applicantOpeningId || !applicantName.trim()) {
      setApplicationFormError('Job opening and candidate name are required');
      return;
    }
    setApplicationBusy(true);
    setApplicationFormError(null);
    try {
      await hrService.createJobApplication({
        jobOpeningId: applicantOpeningId,
        candidateName: applicantName,
        email: applicantEmail || undefined,
        phone: applicantPhone || undefined,
      });
      setApplicationDialogOpen(false);
      loadApplications();
    } catch (err: any) {
      setApplicationFormError(err.message || 'Failed to add application');
    } finally {
      setApplicationBusy(false);
    }
  };

  const handleStageChange = async (application: JobApplication, event: SelectChangeEvent) => {
    const newStage = event.target.value as JobApplicationStage;
    if (newStage === application.stage) return;
    setStageUpdating(application.id);
    try {
      const updated = await hrService.updateJobApplicationStage(application.id, newStage);
      setApplications((prev) => prev.map((a) => (a.id === application.id ? updated : a)));
    } catch (err: any) {
      setApplicationsError(err.message || 'Failed to update stage');
    } finally {
      setStageUpdating(null);
    }
  };

  return (
    <Box>
      <PageHeader title="Recruitment" subtitle="Job openings and applications by pipeline stage" />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard title="Open Positions" value={summary?.openPositions ?? null} icon={<WorkOutline />} color="#7c3aed" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard title="Job Openings" value={openings.length} icon={<PersonSearch />} color="#0284c7" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard title="Applications" value={applications.length} icon={<Groups />} color="#0d9488" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard title="Hired" value={stageCounts.HIRED} icon={<EmojiEvents />} color="#059669" />
        </Grid>
      </Grid>

      {/* Job openings */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Job Openings
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={loadOpenings} disabled={openingsLoading}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button variant="contained" size="small" startIcon={<Add />} onClick={openOpeningDialog}>
            New Opening
          </Button>
        </Box>
      </Box>

      <FilterBar>
        <Chip label={`All (${openings.length})`} size="small" color={openingStatusFilter === 'ALL' ? 'primary' : 'default'} variant={openingStatusFilter === 'ALL' ? 'filled' : 'outlined'} onClick={() => setOpeningStatusFilter('ALL')} />
        {(Object.keys(openingStatusLabel) as JobOpeningStatus[]).map((s) => (
          <Chip
            key={s}
            size="small"
            label={openingStatusLabel[s]}
            color={openingStatusFilter === s ? 'primary' : 'default'}
            variant={openingStatusFilter === s ? 'filled' : 'outlined'}
            onClick={() => setOpeningStatusFilter(s)}
          />
        ))}
        <FilterSpacer />
        <Typography variant="body2" color="text.secondary">
          <strong>{filteredOpenings.length}</strong> openings
        </Typography>
      </FilterBar>

      {openingsError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setOpeningsError(null)}>
          {openingsError}
        </Alert>
      )}

      <TableSurface>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                {['Job Code', 'Title', 'Department', 'Openings', 'Status', 'Target Date'].map((h, i) => (
                  <TableCell key={h} align={i === 3 ? 'right' : 'left'} sx={headerCellSx}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {openingsLoading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6, border: 0 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : filteredOpenings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ border: 0 }}>
                    <TableEmptyState title="No job openings match this filter" />
                  </TableCell>
                </TableRow>
              ) : (
                filteredOpenings.map((o) => (
                  <TableRow key={o.id} sx={bodyRowSx}>
                    <TableCell sx={{ py: 1.5 }}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                        {o.job_code}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {o.title}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{o.dept_name || '—'}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {o.openings}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StatusDot label={openingStatusLabel[o.status]} tone={openingStatusTone[o.status]} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{o.target_date ? new Date(o.target_date).toLocaleDateString('en-IN') : '—'}</Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TableSurface>

      {/* Applications */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, mt: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Applications by Stage
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={loadApplications} disabled={applicationsLoading}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button variant="contained" size="small" startIcon={<Add />} onClick={openApplicationDialog}>
            New Application
          </Button>
        </Box>
      </Box>

      <FilterBar>
        <Chip label={`All (${applications.length})`} size="small" color={stageTab === 'ALL' ? 'primary' : 'default'} variant={stageTab === 'ALL' ? 'filled' : 'outlined'} onClick={() => setStageTab('ALL')} />
        {stages.map((s) => (
          <Chip
            key={s}
            size="small"
            label={`${stageLabel[s]} (${stageCounts[s]})`}
            color={stageTab === s ? 'primary' : 'default'}
            variant={stageTab === s ? 'filled' : 'outlined'}
            onClick={() => setStageTab(s)}
          />
        ))}
        <TextField
          select
          size="small"
          label="Job Opening"
          value={openingFilter}
          onChange={(e) => setOpeningFilter(e.target.value === '' ? '' : Number(e.target.value))}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">All Openings</MenuItem>
          {openings.map((o) => (
            <MenuItem key={o.id} value={o.id}>
              {o.job_code} — {o.title}
            </MenuItem>
          ))}
        </TextField>
        <FilterSpacer />
        <Typography variant="body2" color="text.secondary">
          <strong>{filteredApplications.length}</strong> applications
        </Typography>
      </FilterBar>

      {applicationsError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setApplicationsError(null)}>
          {applicationsError}
        </Alert>
      )}

      <TableSurface>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                {['Candidate', 'Job Opening', 'Contact', 'Applied On', 'Stage'].map((h) => (
                  <TableCell key={h} sx={headerCellSx}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {applicationsLoading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6, border: 0 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : filteredApplications.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ border: 0 }}>
                    <TableEmptyState title="No applications in this stage" hint="Try a different stage or opening filter." />
                  </TableCell>
                </TableRow>
              ) : (
                filteredApplications.map((a) => (
                  <TableRow key={a.id} sx={bodyRowSx}>
                    <TableCell sx={{ py: 1.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {a.candidate_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{a.job_title}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        {a.job_code}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{a.email || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {a.phone || ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{new Date(a.created_at).toLocaleDateString('en-IN')}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <StatusDot label={stageLabel[a.stage]} tone={stageTone[a.stage]} />
                        <Select
                          size="small"
                          value={a.stage}
                          onChange={(e) => handleStageChange(a, e)}
                          disabled={stageUpdating === a.id}
                          sx={{ minWidth: 140, '& .MuiSelect-select': { py: 0.5, fontSize: '0.8125rem' } }}
                        >
                          {stages.map((s) => (
                            <MenuItem key={s} value={s}>
                              Move to {stageLabel[s]}
                            </MenuItem>
                          ))}
                        </Select>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TableSurface>

      {/* New job opening */}
      <Dialog open={openingDialogOpen} onClose={() => (openingBusy ? undefined : setOpeningDialogOpen(false))} maxWidth="sm" fullWidth>
        <DialogTitle>New Job Opening</DialogTitle>
        <DialogContent dividers>
          {openingFormError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {openingFormError}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 0.25 }}>
            <Grid size={12}>
              <TextField fullWidth size="small" label="Title" required value={openingTitle} onChange={(e) => setOpeningTitle(e.target.value)} />
            </Grid>
            <Grid size={6}>
              <TextField
                select
                fullWidth
                size="small"
                label="Department"
                value={openingDept}
                onChange={(e) => setOpeningDept(e.target.value === '' ? '' : Number(e.target.value))}
              >
                <MenuItem value="">—</MenuItem>
                {departments.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.dept_name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth
                size="small"
                label="Number of Openings"
                type="number"
                value={openingCount}
                onChange={(e) => setOpeningCount(Math.max(1, Number(e.target.value)))}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                size="small"
                label="Target Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={openingTargetDate}
                onChange={(e) => setOpeningTargetDate(e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpeningDialogOpen(false)} disabled={openingBusy}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreateOpening} disabled={openingBusy}>
            {openingBusy ? 'Creating…' : 'Create Opening'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* New application */}
      <Dialog open={applicationDialogOpen} onClose={() => (applicationBusy ? undefined : setApplicationDialogOpen(false))} maxWidth="sm" fullWidth>
        <DialogTitle>New Application</DialogTitle>
        <DialogContent dividers>
          {applicationFormError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {applicationFormError}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 0.25 }}>
            <Grid size={12}>
              <TextField
                select
                fullWidth
                size="small"
                label="Job Opening"
                required
                value={applicantOpeningId}
                onChange={(e) => setApplicantOpeningId(e.target.value === '' ? '' : Number(e.target.value))}
              >
                <MenuItem value="">—</MenuItem>
                {openings.map((o) => (
                  <MenuItem key={o.id} value={o.id}>
                    {o.job_code} — {o.title}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={12}>
              <TextField fullWidth size="small" label="Candidate Name" required value={applicantName} onChange={(e) => setApplicantName(e.target.value)} />
            </Grid>
            <Grid size={6}>
              <TextField fullWidth size="small" label="Email" type="email" value={applicantEmail} onChange={(e) => setApplicantEmail(e.target.value)} />
            </Grid>
            <Grid size={6}>
              <TextField fullWidth size="small" label="Phone" value={applicantPhone} onChange={(e) => setApplicantPhone(e.target.value)} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApplicationDialogOpen(false)} disabled={applicationBusy}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreateApplication} disabled={applicationBusy}>
            {applicationBusy ? 'Adding…' : 'Add Application'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RecruitmentPage;
